<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-pricelist-glass">
        <i class="far fa-money-check-edit-alt"></i> Glass
      </Tab>
      <Tab tabid="tab-pricelist-service">
        <i class="far fa-tools"></i> Glass Services
      </Tab>
      <Tab tabid="tab-pricelist-general">
        <i class="far fa-cash-register"></i> General
      </Tab>
      <Tab tabid="tab-pricelist-notes">
        <i class="far fa-sticky-note"></i> Notes
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-pricelist-glass">
        <div v-if="editing" class="float-right">
          <ActionDropMenu>
            <DropdownMenuItem @click="resetGlassSettings" icon="fa-trash-restore">Reset Glass Settings</DropdownMenuItem>
          </ActionDropMenu>
        </div>
        <PriceListConfigList
          stockType="glass"
          :isCustomer="isCustomer"
          :priceListConfig="configData.configList"
          :priceGroupList="stockPriceGroupList"
          @update="updateConfigList($event)"
          :editing="editing"
        />
      </TabPage>
      <TabPage tabid="tab-pricelist-service">
        <div v-if="editing" class="float-right">
          <ActionDropMenu>
            <DropdownMenuItem @click="resetGlassServiceSettings" icon="fa-trash-restore">Reset Glass Service Settings</DropdownMenuItem>
          </ActionDropMenu>
        </div>
        <PriceListConfigList
          stockType="glassService"
          :isCustomer="isCustomer"
          :priceListConfig="configData.configList"
          :priceGroupList="stockPriceGroupList"
          @update="updateConfigList($event)"
          :editing="editing"
        />
      </TabPage>
      <TabPage tabid="tab-pricelist-general">
        <div v-if="editing" class="float-right">
          <ActionDropMenu>
            <DropdownMenuItem @click="resetGeneralSettings" icon="fa-trash-restore">Reset General Settings</DropdownMenuItem>
          </ActionDropMenu>
        </div>
        <PriceListConfigList
          stockType="general"
          :isCustomer="isCustomer"
          :priceListConfig="configData.configList"
          :priceGroupList="stockPriceGroupList"
          @update="updateConfigList($event)"
          :editing="editing"
        />
      </TabPage>
      <TabPage tabid="tab-pricelist-notes">
        <div class="row mb-2">
          <div class="col-lg-12">
            <div v-if="editing" style="clear: both;">
              <ActionDropMenu class="float-right">
                <DropdownMenuItem @click="resetNotesSettings" icon="fa-trash-restore">Reset Notes Settings</DropdownMenuItem>
                <DropdownMenuItem v-if="isCustomer" @click="copyGlobalNotes" icon="fa-copy">Copy Global Notes</DropdownMenuItem>
              </ActionDropMenu>
            </div>
          </div>
        </div>
        <FormRow label="Price List Heading" helpTip="Displayed on the top of the price list">
          <FormTextEditor v-if="editing" :value="getOptionTextValue('headingText')" @input="updateOptionValue('headingText', $event)" />
          <FormStaticLabel v-if="!editing">
            <div class="text-editor-static-display" v-html="getOptionTextValue('headingText')" />
            <div v-if="isCustomer && !getOptionTextValue('headingText')" class="text-editor-static-display price-list-option-inherited" v-html="getOptionTextValue('headingText', true)" />
          </FormStaticLabel>
        </FormRow>
        <FormRow label="Price List Notes" helpTip="Displayed at the end of the price list, above the notes">
          <FormTextEditor v-if="editing" :value="getOptionTextValue('reportNotes')" @input="updateOptionValue('reportNotes', $event)" />
          <FormStaticLabel v-if="!editing">
            <div class="text-editor-static-display" v-html="getOptionTextValue('reportNotes')" />
            <div v-if="isCustomer && !getOptionTextValue('reportNotes')" class="text-editor-static-display price-list-option-inherited" v-html="getOptionTextValue('reportNotes', true)" />
          </FormStaticLabel>
        </FormRow>
        <FormRow label="Notes Title" helpTip="Displayed above the notes">
          <FormTextEditor v-if="editing" :value="getOptionTextValue('noteListTitle')" @input="updateOptionValue('noteListTitle', $event)" />
          <FormStaticLabel v-if="!editing">
            <div class="text-editor-static-display" v-html="getOptionTextValue('noteListTitle')" />
            <div v-if="isCustomer && !getOptionTextValue('noteListTitle')" class="text-editor-static-display price-list-option-inherited" v-html="getOptionTextValue('noteListTitle', true)" />
          </FormStaticLabel>
        </FormRow>
        <FormRow label="Price List Page Footer" helpTip="Displays on the footer of each page as well as in the notes">
          <FormInputText v-if="editing" :value="getOptionTextValue('footerText')" @input="updateOptionValue('footerText', $event)" />
          <FormStaticLabel v-if="!editing">
            <div v-html="getOptionTextValue('footerText')" />
            <div v-if="isCustomer && !getOptionTextValue('footerText')" class="text-editor-static-display price-list-option-inherited" v-html="getOptionTextValue('footerText', true)" />
          </FormStaticLabel>
        </FormRow>

        <FormRow label="Show price group minimum charges" helpTip="Displays the minimum charge details for price groups in the notes">
          <span v-if="!editing">
            <FormStaticLabel>
              <i class="fas fa-check permission-allowed" v-if="getOptionSwitchValue('priceGroupDisplay') == 'checked'"> </i>
              <i class="fas fa-minus permission-inherited" v-if="getOptionSwitchValue('priceGroupDisplay') == 'indeterminate'"> </i>
              <i class="fas fa-ban permission-denied" v-if="getOptionSwitchValue('priceGroupDisplay') == 'unchecked'"> </i>
            </FormStaticLabel>
          </span>
          <span v-else>
            <FormSwitch :value="getOptionSwitchValue('priceGroupDisplay')" :inherited="getOptionInheritedValue('priceGroupDisplay')" @update="updateOptionSwitchValue('priceGroupDisplay', $event)" />
          </span>
        </FormRow>

        <FormRow label="Show delivery details" helpTip="Displays the default delivery type and price in the notes">
          <span v-if="!editing">
            <FormStaticLabel>
              <i class="fas fa-check permission-allowed" v-if="getOptionSwitchValue('deliveryInfoDisplay') == 'checked'"> </i>
              <i class="fas fa-minus permission-inherited" v-if="getOptionSwitchValue('deliveryInfoDisplay') == 'indeterminate'"> </i>
              <i class="fas fa-ban permission-denied" v-if="getOptionSwitchValue('deliveryInfoDisplay') == 'unchecked'"> </i>
            </FormStaticLabel>
          </span>
          <span v-else>
            <FormSwitch :value="getOptionSwitchValue('deliveryInfoDisplay')" :inherited="getOptionInheritedValue('deliveryInfoDisplay')" @update="updateOptionSwitchValue('deliveryInfoDisplay', $event)" />
          </span>
        </FormRow>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<style scoped>
.price-list-option-inherited {
  font-style: italic;
  color: #979da8;
}
</style>

<script>
import _ from 'lodash'
import TabbedBox from "@/components/layout/tabbedBox.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"
import FormSwitch from '@/components/form/formSwitch.vue'   // Used for setting enum

import { InventoryStockTypes, PriceListConfigDisplayTypes, PriceListConfigEntryTypes } from '@/store/types'

import PriceListConfigList from '@/modules/pricing/components/priceListConfigList.vue'

export default {
  name: 'PriceListConfigEditor',
  components: {
    TabbedBox, 
    Tab,
    TabPage,
    PriceListConfigList
  },
  props: {
    isCustomer: {
      type: Boolean,
      default: false
    },
    editing: Boolean,
    stockPriceGroupList: Array,
    config: Object
  },
  watch: {
    config: function(newVal, oldVal) {
      this.$data.configData = _.cloneDeep(newVal)
    }
  },
  data() {
    return {
      configData: {}
    }
  },
  methods: {
    updateConfigList(configList) {
      this.$data.configData.configList = configList
      this.$emit('update', this.$data.configData)
    },
    updateOptionValue(field, value) {
      if(this.$props.isCustomer)
        this.$data.configData.reportOptions[field].customerDisplay = value
      else
        this.$data.configData.reportOptions[field].display = value
      this.$emit('updateOptions', this.$data.configData.reportOptions)
    },
    updateOptionSwitchValue(field, value) {
      this.updateOptionValue(field, (value == FormSwitch.ValueSettings.checked ? PriceListConfigDisplayTypes.show : PriceListConfigDisplayTypes.hide))
    },
    getOptionSwitchValue(optionField) {
      var optionItem = _.get(this.$data.configData, `reportOptions.${optionField}`)
      if(!optionItem)
        return FormSwitch.ValueSettings.indeterminate

      if(optionItem.display == PriceListConfigDisplayTypes.show)
        return FormSwitch.ValueSettings.checked
      else if(optionItem.display == PriceListConfigDisplayTypes.hide)
        return FormSwitch.ValueSettings.unchecked
      else if(optionItem.display == PriceListConfigDisplayTypes.mixed)
        return FormSwitch.ValueSettings.indeterminate

      return FormSwitch.ValueSettings.indeterminate
    },
    getOptionInheritedValue(optionField) {
      var optionItem = _.get(this.$data.configData, `reportOptions.${optionField}`)
      if(!optionItem)
        return false

      return optionItem.customerDisplay == PriceListConfigDisplayTypes.inherit
    },
    getOptionTextValue(optionField, getDisplayValue = false) {
      var display = (this.$props.isCustomer && !getDisplayValue) ? 'customerDisplay' : 'display'
      var optionItem = _.get(this.$data.configData, `reportOptions.${optionField}.${display}`)
      if(!optionItem)
        return ''

      return optionItem
    },

    resetGlassSettings() {
      this.resetStockSettings(InventoryStockTypes.glass)
      this.$emit('update', this.$data.configData)
    },
    resetGlassServiceSettings() {
      this.resetStockSettings(InventoryStockTypes.glassService)
      this.$emit('update', this.$data.configData)
    },
    resetGeneralSettings() {
      this.resetStockSettings(InventoryStockTypes.general)
      this.$emit('update', this.$data.configData)
    },
    resetNotesSettings() {
      if(this.$props.isCustomer) {
        this.$data.configData.reportOptions.priceGroupDisplay.customerDisplay = PriceListConfigDisplayTypes.inherit
        this.$data.configData.reportOptions.deliveryInfoDisplay.customerDisplay = PriceListConfigDisplayTypes.inherit
      } else {
        this.$data.configData.reportOptions.priceGroupDisplay.globalDisplay = PriceListConfigDisplayTypes.show
        this.$data.configData.reportOptions.deliveryInfoDisplay.globalDisplay = PriceListConfigDisplayTypes.show
      }

      this.$data.configData.reportOptions.priceGroupDisplay.display = this.$data.configData.reportOptions.priceGroupDisplay.globalDisplay
      this.$data.configData.reportOptions.deliveryInfoDisplay.display = this.$data.configData.reportOptions.deliveryInfoDisplay.globalDisplay

      this.updateOptionValue('headingText', '')
      this.updateOptionValue('reportNotes', '')
      this.updateOptionValue('footerText', '')
      this.updateOptionValue('noteListTitle', '')

      this.$emit('update', this.$data.configData)
    },
    copyGlobalNotes() {
      this.updateOptionValue('headingText', _.get(this.$data.configData, 'reportOptions.headingText.globalDisplay'))
      this.updateOptionValue('reportNotes', _.get(this.$data.configData, 'reportOptions.reportNotes.globalDisplay'))
      this.updateOptionValue('footerText', _.get(this.$data.configData, 'reportOptions.footerText.globalDisplay'))
      this.updateOptionValue('noteListTitle', _.get(this.$data.configData, 'reportOptions.noteListTitle.globalDisplay'))
    },
    resetStockSettings(stockType) {

      // Set all stock to customer inherit
      this.$data.configData.configList = this.$data.configData.configList.map(configItem => {
        if(configItem.stockType != stockType)
          return configItem

        configItem.alias = null

        if(configItem.type == PriceListConfigEntryTypes.stock) {
          if(configItem.settings) {
            configItem.settings.forEach(setting => {
              if(this.$props.isCustomer)
                setting.customerDisplay = PriceListConfigDisplayTypes.inherit
              else
                setting.globalDisplay = PriceListConfigDisplayTypes.show
              
              setting.display = setting.globalDisplay
            })
          }
        }
        return configItem
      })
      
    }
  }


}
</script>